import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ExternalLink, Github, Code, Palette, Zap, Server, LockKeyholeIcon } from 'lucide-react'

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const projects = [
    {
      title: "Amazon Web Services: Access Analyzer",
      company: "SDE internship",
      year: "summer '25",
      description: "Producing intra-account and org. access findings. Improved resource support by 65%+, designed & implemented feature flag system. IAM Access Analyzer team.",
      technologies: ["Java", "Go", "CDK", "feature flags","CI/CD pipelines"],
      more: "https://aws.amazon.com/iam/access-analyzer/"
      // category: "backend, security"
    },
    {
      title: "Modeling Opinion Polarization with Hypergraph-based Interactions",
      company: "Research",
      year: "2024",
      description: "Simulating opinion diffusion using hypergraphs. Extended bounded confidence model & generated networks from empirical data. Contact for full paper.",
      technologies: ["Python", "Graph Theory", "Computational Simulation"]
      //category: "3d"
    },
    {
      title: "Bioinformatics @ Stanford Medicine",
      company: "Research internship",
      year: "2023 - 2024",
      description: "Identifying osteoarthritis biomarkers; analyzed 100k+ RNA-seq samples. Identified a promising therapeutic target and presented findings at ORS 2024.",
      technologies: ["R", "Data Visualization", "Principal Component Analysis"],
      more: "https://drive.google.com/file/d/1hRyyVjoF-hf3GEqMZ96Q1WKi8nApOt5y/view?usp=sharing"
      //category: "ai"
    },
    {
      title: "Diffusion Models Optimization",
      company: "Research",
      year: "2023",
      description: "Enhancing open-source text-to-image generator. Improving efficiency + performance using facial feature detection, cut down avg. generation time by over 40%. Contact for full paper.",
      technologies: ["OpenCV", "Diffusion Models", "Generative AI", "Python"],
      //category: "mobile"
    },
    {
      title: "College Kickstart",
      company: "Software & Data Science Internship",
      year: "summer '24",
      description: "Interactive dashboards for college counselors. Processed & organized 25k+ data points for 2023–24 admissions cycle, blog & newsletter content w/ thousands of readers.",
      technologies: ["Javascript", "CSS", "Data Processing", "HTML"],
      //category: "data"
    },
    {
      title: "Draw It! Human v. AI Pictionary",
      company: "CSCI 1430 Computer Vision",
      year: "spring '25",
      description: "Full-stack pictionary game designed for 2-player vs. AI gameplay. Dataset cleaning & augmentation, model architecture & training. Teammates: Alesandro Gopal, Joshua Kou; contact for full report.",
      technologies: ["CNNs", "Data Augmentation", "Python", "Model Training"],
      live: "https://drive.google.com/file/d/16iXf_fkkr9LBe8u9rNPVbW53tcFB9Nge/view?usp=sharing"
      //category: "creative"
    },
    {
      title: "Dynamic Image Resizer",
      company: "project",
      year: "fall '24",
      description: "Smartly resizing by removing lowest-contrast seams.",
      technologies: ["dynamic programming", "memoization"],
      //live: "tbd"
      //category: "creative"
    },
    {
      title: "Console Snake Game",
      company: "project",
      year: "spring '25",
      description: "Snake game built in C.",
      technologies: ["playing snake to debug", "eating food"],
      live: "https://drive.google.com/file/d/1S6e3MWQAerl92au0KYofA7titUE66w-f/view?usp=sharing"
      //category: "creative"
    },
    {
      title: "Multithreaded KV Store",
      company: "project",
      year: "spring '25",
      description: "Implemented a multithreaded key-value store with sharding. R/W locks, mutexes, and more.",
      technologies: ["multithreading", "sharding", "synchronization primitives"],
      //category: "creative"
    }
    
    
  ]

  // const getCategoryIcon = (category: string) => {
  //   switch (category) {
  //     case 'backend, security': return <LockKeyholeIcon size={20} />
  //     case 'web': return <Code size={20} />
  //     case '3d': return <Palette size={20} />
  //     case 'ai': return <Zap size={20} />
  //     case 'mobile': return <Code size={20} />
  //     case 'data': return <Code size={20} />
  //     case 'creative': return <Palette size={20} />
  //     default: return <Code size={20} />
  //   }
  // }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  }

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  return (
    <div className="container-max" ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="space-y-16"
      >
        {/* Header */}
        <motion.div
          variants={cardVariants}
          className="space-y-4"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary"
          style={{ marginBottom: '1rem' }}>
            projects
          </h2>
          <p className="text-xl text-muted font-mono max-w-2xl">
            a collection of projects, internships, and experiments
          </p>
        </motion.div>

        {/* All Projects Grid */}
        <motion.div variants={cardVariants} className="space-y-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className="group relative overflow-hidden rounded-xl bg-white/30 backdrop-blur border border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="p-6 space-y-4">
                  
                  <h4 className="-mb-px-10 text-lg font-semibold text-primary group-hover:text-primary-400 transition-colors">
                    {project.title}
                  </h4>
                  <p className="-mt-px-10 text-sm italic text-muted -mt-2 mb-1">
                    {project.company} &middot; {project.year}
                  </p>
                  <p className="text-muted text-sm leading-relaxed pt-1">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1.5">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 text-muted bg-white/60 text-xs rounded-md border border-white/40 hover:border-rose-300/50 transition-colors"
                      >
                        {tech}
                      </span>
                    ))}
                    
                  </div>
                  
                  <div className="flex gap-3 pt-1">
                    {project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-1.5 text-muted hover:text-primary transition-colors text-sm"
                      >
                        <Github size={16} />
                        <span>Code</span>
                      </motion.a>
                    )}
                    {project.live && (
                      <motion.a
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-1.5 text-muted hover:text-primary transition-colors text-sm"
                      >
                        <ExternalLink size={16} />
                        <span>Demo</span>
                      </motion.a>
                    )}
                    {project.more && (
                      <motion.a
                        href={project.more}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-1.5 text-muted hover:text-primary transition-colors text-sm"
                      >
                        <ExternalLink size={16} />
                        <span>More</span>
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Projects 